import React, { createContext, useContext, useState, useCallback, useRef } from "react"
import type { LiteracyProgress, DrillAttempt } from "../types/literacy"
import { calculateAccuracy, isMasteryAchieved } from "../components/literacy/mastery"
import { curriculum, getLessonById, getNextLesson } from "../components/literacy/curriculum"
import { literacy as literacyApi } from "../api/endpoints"

// ── Types ────────────────────────────────────────────────────────────────────

interface LiteracyContextValue {
  /** Full progress tree for the current patient */
  progress: LiteracyProgress | null
  /** Whether progress is being fetched */
  loading: boolean
  /** Fetch progress from backend for a patient */
  loadProgress: (patientId: string) => Promise<void>
  /** Record a drill attempt and update local + remote state */
  recordAttempt: (
    patientId: string,
    lessonId: string,
    drillType: string,
    attempt: DrillAttempt,
  ) => Promise<void>
  /** Current phase ID (convenience) */
  currentPhaseId: string | null
  /** Current lesson ID (convenience) */
  currentLessonId: string | null
}

const LiteracyContext = createContext<LiteracyContextValue | null>(null)

// ── Default empty progress ───────────────────────────────────────────────────

function createEmptyProgress(): LiteracyProgress {
  const firstPhase = curriculum.phases[0]
  const firstLesson = firstPhase?.lessons[0]
  return {
    phases: {},
    currentPhaseId: firstPhase?.id ?? "",
    currentLessonId: firstLesson?.id ?? "",
    totalTimeSpent: 0,
    lastActivity: Date.now(),
  }
}

// ── Provider ─────────────────────────────────────────────────────────────────

export function LiteracyProvider({ children }: { children: React.ReactNode }) {
  const [progress, setProgress] = useState<LiteracyProgress | null>(null)
  const [loading, setLoading] = useState(false)
  const loadedPatientRef = useRef<string | null>(null)

  const loadProgress = useCallback(async (patientId: string) => {
    // Skip if already loaded for this patient
    if (loadedPatientRef.current === patientId && progress) return

    setLoading(true)
    try {
      const res = await literacyApi.getProgress(patientId)
      if (res.data?.progress) {
        setProgress(res.data.progress as unknown as LiteracyProgress)
      } else {
        setProgress(createEmptyProgress())
      }
      loadedPatientRef.current = patientId
    } catch {
      // First time — no progress exists yet
      setProgress(createEmptyProgress())
      loadedPatientRef.current = patientId
    } finally {
      setLoading(false)
    }
  }, [progress])

  const recordAttempt = useCallback(
    async (
      patientId: string,
      lessonId: string,
      drillType: string,
      attempt: DrillAttempt,
    ) => {
      try {
        const res = await literacyApi.recordDrillAttempt(patientId, {
          lessonId,
          drillType,
          attempt,
        })
        if (res.data?.progress) {
          setProgress(res.data.progress as unknown as LiteracyProgress)
        }
      } catch (err) {
        console.error("[LiteracyContext] Failed to record attempt:", err)
      }
    },
    [],
  )

  const value: LiteracyContextValue = {
    progress,
    loading,
    loadProgress,
    recordAttempt,
    currentPhaseId: progress?.currentPhaseId ?? null,
    currentLessonId: progress?.currentLessonId ?? null,
  }

  return (
    <LiteracyContext.Provider value={value}>
      {children}
    </LiteracyContext.Provider>
  )
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useLiteracy() {
  const ctx = useContext(LiteracyContext)
  if (!ctx) {
    throw new Error("useLiteracy must be used within a LiteracyProvider")
  }
  return ctx
}

export default LiteracyContext
